import React, { useEffect, useState } from 'react';
import hwCarbon from 'assets/images/hw-carbon.png';
import hwCarbonWebp from 'assets/images/hw-carbon.png?as=webp';
import Slider from 'react-slick';
import loadable from '@loadable/component';
import LazyLoad from 'react-lazyload';
import { PRODUCT_VARIANT_ABBREVIATIONS } from '../../../shared/enums';
import { filterDetailStatic } from '../../static-data';

const FadeIn = loadable(() => import('../common/FadeIn'));
const HWSlideInner = loadable(() => import('../common/HWSlideInner'));
const CCSlideInner = loadable(() => import('../common/CCSlideInner'));
const MBSlideInner = loadable(() => import('../common/MBSlideInner'));
const ORSlideInner = loadable(() => import('../common/ORSlideInner'));
const RPSlideInner = loadable(() => import('../common/RPSlideInner'));

/**
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const NextArrow = props => {
  const { className, onClick } = props;

  return (
    <button type='button' className={`${className} filter-detail__slide-next`} onClick={onClick}>
      <svg className='icon icon-circle-right-arrow'><use xlinkHref='#icon-circle-right-arrow' /></svg>
    </button>
  );
};

/**
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const PrevArrow = props => {
  const { className, onClick } = props;

  return (
    <button type='button' className={`${className} filter-detail__slide-prev`} onClick={onClick}>
      <svg className='icon icon-circle-left-arrow'><use xlinkHref='#icon-circle-left-arrow' /></svg>
    </button>
  );
};

/**
 * @param settingsImages
 * @param asNavFor
 * @param setNav1
 * @param sliderItems
 * @param prop4
 * @returns {JSX.Element}
 * @constructor
 */
const FilterDetailImageSlider = ({
  settingsImages, asNavFor, setNav1, sliderItems, prop4,
}) => (
  <Slider
    {...settingsImages}
    asNavFor={asNavFor}
    ref={slider => setNav1(slider)}
    className='filter-detail__image-slider'
  >
    {sliderItems?.map(prop4)}
  </Slider>
);

/**
 * @param settingsItems
 * @param asNavFor
 * @param setNav2
 * @param inner
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const FilterDetailSlider = ({
  settingsItems, asNavFor, setNav2, inner, children,
}) => (
  <Slider
    {...settingsItems}
    asNavFor={asNavFor}
    ref={slider => setNav2(slider)}
    className={`filter-detail__slider ${inner ? 'filter-detail__slider--has-detail' : ''}`}
  >
    {React.Children.toArray(children)}
  </Slider>
);

/**
 * @returns {JSX.Element}
 * @constructor
 */
const FilterDetailImageSlide = () => (
  <figure className='filter-detail__image-slide'>
    <LazyLoad>
      <FadeIn>
        <picture>
          <source srcSet={hwCarbonWebp} type='image/webp' />
          <source srcSet={hwCarbon} type='image/png' />
          <img src={hwCarbon} alt='hw-carbon' />
        </picture>
      </FadeIn>
    </LazyLoad>
  </figure>
);

/**
 * @param dots
 * @returns {JSX.Element}
 * @constructor
 */
const Dots = dots => <ul>{dots}</ul>;

/**
 * @param page
 * @returns {JSX.Element}
 * @constructor
 */
const FilterDetail = ({ page }) => {
  const [ isInner, setIsInner ] = useState(false);
  const [{ intro, sliderItems }, setFilterDetail ] = useState(filterDetailStatic[page]
    || filterDetailStatic[PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER]);

  const [ nav1, setNav1 ] = useState();
  const [ nav2, setNav2 ] = useState();

  const moreDetailsHandle = condition => {
    setIsInner(condition);
  };

  const innerSlides = {
    BB: null,
    BS: null,
    CC: <CCSlideInner isInner={isInner} moreDetailsHandle={moreDetailsHandle} />,
    HW: <HWSlideInner isInner={isInner} moreDetailsHandle={moreDetailsHandle} />,
    MB: <MBSlideInner isInner={isInner} moreDetailsHandle={moreDetailsHandle} />,
    OR: <ORSlideInner isInner={isInner} moreDetailsHandle={moreDetailsHandle} />,
    RP: <RPSlideInner isInner={isInner} moreDetailsHandle={moreDetailsHandle} />,
  };

  useEffect(() => {
    setFilterDetail(filterDetailStatic[page]);
  }, [ page ]);

  const settingsImages = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    infinite: true,
    fade: true,
    speed: 1000,
    autoplay: false,
    autoplaySpeed: 2000,
    pauseOnHover: false,
    pauseOnFocus: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    beforeChange: (oldIdx, newIdx) => {
      if (oldIdx !== newIdx && isInner) {
        moreDetailsHandle(false);
        setTimeout(() => {
          nav2.slickNext();
        }, 200);
      }
    },
    responsive: [{
      breakpoint: 767,
      settings: {
        arrows: false,
      },
    }],
  };

  const settingsItems = {
    dots: false,
    arrows: false,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    infinite: true,
    autoplaySpeed: 2000,
    centerMode: true,
    centerPadding: '305px',
    pauseOnHover: false,
    pauseOnFocus: false,
    focusOnSelect: true,
    appendDots: Dots,
    responsive: [{
      breakpoint: 1441,
      settings: {
        centerPadding: '200px',
      },
    }, {
      breakpoint: 767,
      settings: {
        dots: true,
        centerPadding: '50px',
      },
    }],
  };

  return (
    <div className='filter-detail'>
      <div className='container'>
        {intro && (
          <div className='filter-detail__intro'>
            <h3 className='filter-detail__intro-title'>{intro.title}</h3>
            <div className='filter-detail__intro-description'>
              <p>{intro.text}</p>
            </div>
          </div>
        )}
        <FilterDetailImageSlider
          settingsImages={settingsImages}
          asNavFor={nav2}
          setNav1={setNav1}
          sliderItems={sliderItems}
          prop4={FilterDetailImageSlide}
        />
        <FilterDetailSlider
          settingsItems={settingsItems}
          asNavFor={nav1}
          setNav2={setNav2}
          inner={isInner}
        >
          {sliderItems?.map(({ inner, title, text }) => (
            <div className='filter-detail__slide'>
              {inner
                ? innerSlides[page]
                : !isInner && (
                  <>
                    <h2 className='filter-detail__slide-title'>{title}</h2>
                    <p>{text}</p>
                  </>
                )}
            </div>
          ))}
        </FilterDetailSlider>
      </div>
    </div>
  );
};

export default FilterDetail;
